import { characterCadence, typingState, type TypingState } from '../internalTimeline';
import { clamp, easeInOutCubic, easeOutCubic, segment } from '../timeline';
import {
  V3_ASSEMBLE_MS,
  V3_CUES,
  V3_ENTER_DELAY_MS,
  V3_SCENES,
  V3_TRANSITION_MS,
  type V3Cue,
  type V3SceneDef,
  type V3SceneId,
} from './scenes';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const COMPACT_MQ = '(max-width: 979px)';

// Compact desktop-surface pan: settle left-aligned, hold, then travel to the
// right edge so the "Needs attention" panel is fully framed for the spotlight.
const PAN_HOLD_MS = 900;
const PAN_MS = 1500;

const EASES: Record<string, (t: number) => number> = {
  linear: (t) => t,
  outCubic: easeOutCubic,
  inOutCubic: easeInOutCubic,
  outQuint: (t) => 1 - Math.pow(1 - t, 5),
};

const thousands = (n: number) => {
  const v = Math.round(n).toString();
  return v.length > 3 ? `${v.slice(0, -3)},${v.slice(-3)}` : v;
};

interface ScheduledScene extends V3SceneDef {
  start: number;
}

/** The layer a scene plays on: spotlight states share their base scene's layer. */
const layerFor = (id: V3SceneId): string => (id.endsWith('-spot') ? id.slice(0, -5) : id);

/*
 * V3 showcase controller. A single rAF accumulator advances an elapsed clock;
 * scene changes are data-attribute writes whose frame morph and cross-fades
 * ease in the variant stylesheet, while the inside-scene choreography — V2's
 * staged reveals, bar draws, gauge arc and counters, adapted in scenes.ts — is
 * written per frame as --v3r-* properties, exactly like the V2 engine. Plays
 * only in view and on a visible tab, resumes from the same elapsed time, and
 * loops seamlessly (report and prompt share the wide format).
 */
export class ShowcaseV3Engine {
  private static readonly VISIBLE_RATIO = 0.3;

  private readonly root: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly frame: HTMLElement;
  private readonly layers: { id: string; el: HTMLElement }[];
  private readonly counters = new Map<string, { el: HTMLElement; final: string }>();
  private readonly cues = new Map<V3SceneId, V3Cue[]>();
  private readonly cadence: number[];
  private readonly header: HTMLElement | null;
  private readonly schedule: ScheduledScene[];
  private readonly loopMs: number;
  private readonly lastWritten = new Map<string, string>();
  private readonly compactMq = window.matchMedia(COMPACT_MQ);

  private panFrom = 0;
  private panTo = 0;
  private elapsed = 0;
  private lastTs = 0;
  private raf = 0;
  private playing = false;
  private done = false;
  private inView = false;
  private engaged = false;
  private activeId = '';
  private io?: IntersectionObserver;
  private headerIo?: IntersectionObserver;

  constructor(root: HTMLElement) {
    this.root = root;
    this.stage = root.querySelector<HTMLElement>('[data-v3-stage]')!;
    this.frame = root.querySelector<HTMLElement>('[data-v3-frame]')!;
    this.layers = Array.from(root.querySelectorAll<HTMLElement>('[data-v3-layer]')).map((el) => ({
      id: el.dataset.v3Layer ?? '',
      el,
    }));
    const typed = root.querySelector<HTMLElement>('[data-v3-typing]');
    this.cadence = characterCadence(Number(typed?.dataset.length ?? 0));
    this.header = document.querySelector<HTMLElement>('header.site-header');

    for (const cue of V3_CUES) {
      const list = this.cues.get(cue.scene) ?? [];
      list.push(cue);
      this.cues.set(cue.scene, list);
      if (cue.counter) {
        const el = root.querySelector<HTMLElement>(`[data-v3-count="${cue.id}"]`);
        if (el) {
          this.counters.set(cue.id, { el, final: el.textContent ?? '' });
        }
      }
    }

    let at = 0;
    this.schedule = V3_SCENES.map((scene) => {
      const entry = { ...scene, start: at };
      at += scene.holdMs + V3_TRANSITION_MS;
      return entry;
    });
    this.loopMs = at;
  }

  private write(name: string, value: string) {
    if (this.lastWritten.get(name) === value) {
      return;
    }
    this.lastWritten.set(name, value);
    this.stage.style.setProperty(name, value);
  }

  private applyTyping(state: TypingState) {
    this.write('--chars-prompt', `${state.chars}`);
    this.write('--caret-prompt', state.caret.toFixed(3));
    for (let i = 0; i < state.controls.length; i += 1) {
      this.write(`--r-prompt-controls-${i}`, state.controls[i].toFixed(3));
    }
    for (let i = 0; i < state.chips.length; i += 1) {
      this.write(`--r-prompt-chips-${i}`, state.chips[i].toFixed(3));
    }
    this.write('--r-prompt-more', state.more.toFixed(3));
    if (state.sentenceDone) {
      if (!this.root.dataset.hccTyped) {
        this.root.dataset.hccTyped = 'on';
      }
    } else if (this.root.dataset.hccTyped) {
      delete this.root.dataset.hccTyped;
    }
  }

  private setScene(scene: ScheduledScene) {
    if (this.activeId === scene.id) {
      return;
    }
    this.activeId = scene.id;
    this.stage.dataset.v3Scene = scene.id;
    this.stage.dataset.v3Format = scene.format;
    this.stage.dataset.v3Caption = scene.caption;
    const activeLayer = layerFor(scene.id);
    for (const { id, el } of this.layers) {
      const isActive = id === activeLayer;
      el.inert = !isActive;
      el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    }
  }

  private counterText(cue: V3Cue, v: number): string {
    const { from, to, format } = cue.counter!;
    const value = from + (to - from) * v;
    return format === 'percent' ? `${Math.round(value)}%` : thousands(value);
  }

  /** V2's renderInternals, on the showcase clock: staged cues for the active scene. */
  private renderInternals(active: ScheduledScene, elapsed: number) {
    const list = this.cues.get(active.id);
    if (!list) {
      return;
    }
    const assemble = V3_ASSEMBLE_MS[active.id] ?? 1500;
    const l = clamp((elapsed - active.start - V3_ENTER_DELAY_MS) / assemble);
    for (const cue of list) {
      const ease = EASES[cue.ease ?? 'outCubic'];
      if (cue.count && cue.count > 1) {
        const stagger = cue.stagger ?? 0;
        const span = Math.max(cue.to - cue.from - stagger * (cue.count - 1), 0.001);
        for (let i = 0; i < cue.count; i += 1) {
          const from = cue.from + stagger * i;
          this.write(
            `--v3r-${cue.scene}-${cue.id}-${i}`,
            ease(segment(l, from, from + span)).toFixed(3),
          );
        }
        continue;
      }
      const v = ease(segment(l, cue.from, cue.to));
      if (cue.write !== false) {
        this.write(`--v3r-${cue.scene}-${cue.id}`, v.toFixed(3));
      }
      if (cue.counter) {
        const counter = this.counters.get(cue.id);
        const text = this.counterText(cue, v);
        if (counter && counter.el.textContent !== text) {
          counter.el.textContent = text;
        }
      }
    }
  }

  /** Spotlight dim: everything but the "Needs attention" card settles to 40%. */
  private renderQuiet(active: ScheduledScene, elapsed: number) {
    for (const pair of ['desktop', 'mobile'] as const) {
      if (layerFor(active.id) !== pair) {
        continue;
      }
      const dim =
        active.id === `${pair}-spot`
          ? 1 - 0.6 * easeInOutCubic(segment(elapsed - active.start, 150, 640))
          : 1;
      this.write(`--v3-q-${pair}`, dim.toFixed(3));
    }
  }

  /** Left-to-right travel across the desktop surface, pure in elapsed time. */
  private renderPan(active: ScheduledScene, elapsed: number) {
    if (layerFor(active.id) !== 'desktop' || !this.compactMq.matches) {
      return;
    }
    const t =
      active.id === 'desktop-spot'
        ? 1
        : easeInOutCubic(segment(elapsed - active.start, PAN_HOLD_MS, PAN_HOLD_MS + PAN_MS));
    this.write('--v3-dx', `${(this.panFrom + (this.panTo - this.panFrom) * t).toFixed(1)}px`);
  }

  private render(elapsed: number) {
    let active = this.schedule[0];
    for (const scene of this.schedule) {
      if (elapsed >= scene.start) {
        active = scene;
      }
    }
    this.setScene(active);
    this.renderInternals(active, elapsed);
    this.renderQuiet(active, elapsed);
    this.renderPan(active, elapsed);
    if (this.cadence.length > 0) {
      this.applyTyping(typingState(elapsed, this.cadence));
    }
  }

  /** Wipe per-cycle reveals so the restarted loop assembles every scene again. */
  private resetCycle() {
    for (const name of this.lastWritten.keys()) {
      if (name.startsWith('--v3r-')) {
        this.stage.style.removeProperty(name);
        this.lastWritten.delete(name);
      }
    }
    delete this.root.dataset.hccTyped;
  }

  private tick = (ts: number) => {
    if (!this.playing) {
      return;
    }
    // Clamp the delta so a throttled or backgrounded frame cannot jump the story.
    const dt = this.lastTs ? Math.min(ts - this.lastTs, 100) : 0;
    this.lastTs = ts;
    this.elapsed += dt;
    if (this.elapsed >= this.loopMs) {
      this.elapsed -= this.loopMs;
      this.resetCycle();
    }
    this.render(this.elapsed);
    this.raf = requestAnimationFrame(this.tick);
  };

  private play() {
    if (this.done || this.playing) {
      return;
    }
    this.playing = true;
    this.lastTs = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  private pause() {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    cancelAnimationFrame(this.raf);
  }

  private sync = () => {
    if (this.inView && !document.hidden && !this.done) {
      this.play();
    } else {
      this.pause();
    }
  };

  /**
   * Header chrome for the overlay compensation, plus the compact-breakpoint
   * scene units: below 980px each scene keeps its own readable scale instead of
   * the Figma-proportional unit, matching V2's responsive foundations. Values
   * are computed here because CSS cannot divide lengths.
   */
  private measure(): number {
    const headerH = this.header ? this.header.offsetHeight : 0;
    const adminBar = parseFloat(getComputedStyle(document.documentElement).marginTop) || 0;
    const style = document.documentElement.style;
    style.setProperty('--hcc-header-h', `${headerH}px`);
    style.setProperty('--hcc-chrome-offset', `${adminBar + headerH}px`);

    const su = [
      '--v3-su-desktop',
      '--v3-su-phone',
      '--v3-su-email',
      '--v3-su-signage',
      '--v3-su-report',
      '--v3-dx',
    ];
    if (this.compactMq.matches) {
      const vw = window.innerWidth;
      const wideW = vw - (vw <= 768 ? 32 : 48);
      const narrowW = Math.min(vw <= 768 ? 300 : 320, vw * (vw <= 768 ? 0.86 : 0.84));
      const panelH = this.frame.getBoundingClientRect().height || 400;
      const suDesktop = (panelH - 24) / 544;
      this.stage.style.setProperty('--v3-su-desktop', `${suDesktop.toFixed(4)}px`);
      this.stage.style.setProperty('--v3-su-phone', `${((panelH - 28) / 519).toFixed(4)}px`);
      this.stage.style.setProperty(
        '--v3-su-email',
        `${Math.min((panelH - 28) / 517.4, (narrowW - 24) / 264).toFixed(4)}px`,
      );
      this.stage.style.setProperty('--v3-su-signage', `${((wideW - 24) / 450).toFixed(4)}px`);
      this.stage.style.setProperty('--v3-su-report', `${((wideW - 24) / 523).toFixed(4)}px`);
      // Pan travel from the real widths: the frame's wide format vs the surface.
      const frameW = vw <= 768 ? vw - 32 : Math.min(680, vw - 48);
      this.panFrom = Math.max(0, (990 * suDesktop - frameW) / 2);
      this.panTo = -this.panFrom;
    } else {
      for (const name of su) {
        this.stage.style.removeProperty(name);
      }
      this.lastWritten.delete('--v3-dx');
      this.panFrom = 0;
      this.panTo = 0;
    }
    return adminBar + headerH;
  }

  private onResize = () => {
    this.measure();
  };

  /** Play, or hold the settled Prompt poster under reduced motion. */
  private applyMotion = () => {
    if (window.matchMedia(REDUCED_MOTION).matches) {
      this.done = true;
      this.pause();
      // The stylesheet's static fallbacks already draw the finished Prompt; drop
      // the live flag so they take over, and clear any mid-cycle writes.
      delete this.root.dataset.v3Live;
      this.stage.removeAttribute('style');
      this.lastWritten.clear();
      this.activeId = '';
      this.setScene(this.schedule[0]);
      this.root.dataset.hccTyped = 'on';
      this.measure();
    } else {
      this.done = false;
      this.root.dataset.v3Live = 'on';
      this.elapsed = 0;
      this.render(0);
      this.sync();
    }
  };

  engage() {
    if (this.engaged) {
      return;
    }
    this.engaged = true;

    // 'on' arms the overlay header compensation, exactly as the V2 showcase does.
    document.documentElement.dataset.hccJs = 'on';
    this.root.dataset.hccBoot = 'ready';

    const chrome = this.measure();

    this.io = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some(
          (e) => e.isIntersecting && e.intersectionRatio >= ShowcaseV3Engine.VISIBLE_RATIO,
        );
        this.sync();
      },
      { threshold: [0, ShowcaseV3Engine.VISIBLE_RATIO, 0.6] },
    );
    // The frame, not the stage: on the opening fold only the frame's top slice
    // is on screen, and typing must start as soon as the composer is seen.
    this.io.observe(this.frame);

    // Overlay header while the hero is in view; standard once it is fully passed.
    this.headerIo = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          delete document.documentElement.dataset.hccHeader;
        } else {
          document.documentElement.dataset.hccHeader = 'standard';
        }
      },
      { rootMargin: `-${Math.round(chrome)}px 0px 0px 0px`, threshold: 0 },
    );
    this.headerIo.observe(this.root);

    document.addEventListener('visibilitychange', this.sync);
    window.addEventListener('resize', this.onResize);
    this.compactMq.addEventListener('change', this.onResize);

    this.applyMotion();
  }

  reducedMotionChange = () => {
    this.applyMotion();
  };
}
